import { useCallback, useEffect, useState } from 'react';

import { IMainPage } from '@/lib/entities/page';

import { CircleButton } from '@/lib/shared/components/Buttons';
import { OfferCard } from '@/lib/shared/components/Cards';
import { ArrowIcon } from '@/lib/shared/icons';

import { clsx } from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';

import styles from './OffersSlider.module.scss';

interface IProps {
  data: IMainPage | undefined;
  theme?: 'light' | 'dark';
}

const OffersSlider = ({ data, theme = 'light' }: IProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    breakpoints: {
      '(max-width: 996px)': { loop: true },
    },
  });
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const changeSlide = useCallback(() => {
    if (!emblaApi) return;
    const slideNum = emblaApi.selectedScrollSnap();

    setCurrentSlide(() => slideNum);
  }, [emblaApi]);

  useEffect(() => {
    changeSlide();
    if (emblaApi) {
      emblaApi?.on('select', changeSlide);
    }
    return () => {
      emblaApi?.off('select', changeSlide);
    };
  }, [changeSlide, emblaApi]);

  return (
    <div
      className={clsx(
        styles.offersSlider__wrapper,
        styles[`offersSlider__wrapper--${theme}`]
      )}
      ref={emblaRef}
    >
      <div className={styles.offersSlider}>
        {data?.ownProjects.map((ownProject, index) => {
          const onClick = () => {
            emblaApi?.scrollTo(index);
          };
          return (
            <OfferCard
              key={index}
              ownProject={ownProject}
              index={index}
              theme={theme}
              length={data?.ownProjects.length}
              onClick={onClick}
            />
          );
        })}
      </div>
      <div
        className={clsx(
          styles.offersSlider__buttons,
          styles['offersSlider__buttons--mobile']
        )}
      >
        <CircleButton
          name="scroll to previous"
          as={'button'}
          content={<ArrowIcon />}
          theme={theme === 'light' ? 'light' : 'mixed'}
          onClick={scrollPrev}
          styleType="secondary"
          className={clsx(
            styles.offersSlider_reviewPrev,
            currentSlide !== 0 && styles['offersSlider_reviewPrev--visible']
          )}
        />
        <CircleButton
          name="scroll to next"
          as={'button'}
          content={<ArrowIcon isRight />}
          theme={theme === 'light' ? 'light' : 'mixed'}
          onClick={scrollNext}
          styleType="secondary"
          className={clsx(
            styles.offersSlider_reviewNext,
            currentSlide !== (data?.ownProjects?.length || 3) - 1 &&
              styles['offersSlider_reviewNext--visible']
          )}
        />
      </div>
      <div className={styles.offersSlider__buttons}>
        <CircleButton
          name="scroll to previous"
          as={'button'}
          content={<ArrowIcon />}
          theme={theme === 'light' ? 'light' : 'mixed'}
          onClick={scrollPrev}
          className={clsx(
            styles.offersSlider_reviewPrev,
            currentSlide !== 0 && styles['offersSlider_reviewPrev--visible']
          )}
        />
        <CircleButton
          name="scroll to next"
          as={'button'}
          content={<ArrowIcon isRight />}
          theme={theme === 'light' ? 'light' : 'mixed'}
          onClick={scrollNext}
          className={clsx(
            styles.offersSlider_reviewNext,
            currentSlide !== (data?.ownProjects?.length || 3) - 1 &&
              styles['offersSlider_reviewNext--visible']
          )}
        />
      </div>
    </div>
  );
};

export default OffersSlider;
